import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { TimeTrackingRole } from '../../../../../models/time-tracking';
import { AccountService } from '../../../../../services/account/account.service';
import { CallbackResult, CallbackResultStatus } from '../../../../../utils';
import { TimeTrackingService } from '../../../../../services/time-tracking-role/time-tracking.service';
import { AddTimeTrackingRoleModalComponent } from './modals/add-time-tracking-role-modal/add-time-tracking-role-modal.component';
import { EditTimeTrackingRoleModalComponent } from './modals/edit-time-tracking-role-modal/edit-time-tracking-role-modal.component';
import { RemoveTimeTrackingRoleModalComponent } from './modals/remove-time-tracking-role-modal/remove-time-tracking-role-modal.component';
import { OrganizationConfig } from '../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';

@Component({
  selector: 'app-time-tracking-config',
  templateUrl: './time-tracking-config.component.html',
  styleUrls: ['./time-tracking-config.component.css']
})
export class TimeTrackingConfigComponent implements OnInit {

  public orgConfig: OrganizationConfig;
  public roleList: TimeTrackingRole[];

  public loadingOrganizationConfig = false;
  public loadingRoles = false;
  public loadingEdit = false;

  public successMessage = '';
  public errorMessage = '';
  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  public modalResult: CallbackResult = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public CallbackResultStatus = CallbackResultStatus;

  constructor(
    private accountService: AccountService,
    private organizationConfigService: OrganizationConfigService,
    private timeTrackingRoleService: TimeTrackingService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().is_organization_admin) {
      this.errorMessage = 'You do not have permission to configure time tracking.';
      return;
    }

    // Make browser history available to the template.
    this.history = history;

    this.loadOrganizationConfig();
    this.loadRoles();
  }

  /**
   * Load the organization config from the API.
   *
   * We do not use the local-stored copy, in order to avoid pop-in of changed
   * values.
   */
  public loadOrganizationConfig() {
    this.loadingOrganizationConfig = true;

    this.organizationConfigService
      .loadOrganizationConfig()
      .then(() => {
        this.orgConfig = this.organizationConfigService
          .getOrganizationConfig();
        this.loadingOrganizationConfig = false;
      });
  }

  public loadRoles() {
    this.loadingRoles = true;

    this.serverError = null;
    this.errorMessage = '';

    this.timeTrackingRoleService
      .getTimeTrackingRoles(1, 100)
      .then(response => { this.roleList = response.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingRoles = false; });
  }

  public roleResultsPresent() {
    return this.roleList && (this.roleList.length > 0);
  }

  public saveOrganizationConfig() {
    this.loadingEdit = true;

    this.successMessage = '';
    this.errorMessage = '';

    this.organizationConfigService
      .updateOrgConfig({
        time_tracking_enabled: this.orgConfig.time_tracking_enabled
      })
      .then(updatedOrgConf => {
        this.orgConfig = updatedOrgConf;
        this.successMessage = 'Time tracking config updated successfully.';
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to modify time tracking settings.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEdit = false; });
  }

  public openAddRoleModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(AddTimeTrackingRoleModalComponent);
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadRoles();
    };
  }

  public openEditRoleModal(role: TimeTrackingRole) {
    // Create a copy of the role so that the role's info is only
    // updated in the table after it is saved to the API.
    const roleInput: TimeTrackingRole = {...role};

    this.modalResult = null;

    const modalRef = this.modalService.open(EditTimeTrackingRoleModalComponent);
    modalRef.componentInstance.role = roleInput;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadRoles();
    };
  }

  public openRemoveRoleModal(role: TimeTrackingRole) {
    // Create a copy of the role so that the role's info is only
    // updated in the table after it is saved to the API.
    const roleInput: TimeTrackingRole = {...role};

    this.modalResult = null;

    const modalRef = this.modalService.open(RemoveTimeTrackingRoleModalComponent);
    modalRef.componentInstance.role = roleInput;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadRoles();
    };
  }

}
